import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { CheckCircle2, ArrowRight, ArrowLeft } from "lucide-react";
import { api, errorMessage } from "../lib/api";
import { LOAN_PROGRAMS } from "../lib/constants";
import { toast } from "sonner";
import AiScoreBadge from "../components/AiScoreBadge";
import LanguageToggle from "../components/LanguageToggle";
import Logo from "../components/Logo";
import { useT } from "../lib/i18n";

const STEPS = ["Personal", "Experience & Capital", "Property", "Financial"];

export default function BorrowerIntake() {
  const { t } = useT();
  const [params] = useSearchParams();
  const ref = params.get("ref") || null;

  const [step, setStep] = useState(0);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    borrower_name: "", borrower_email: "", borrower_phone: "",
    owns_home: false, property_in_llc: false, llc_name: "",
    num_projects: 0, has_re_experience: false, experience_type: "",
    credit_score: 0, capital_available: 0,
    property_address: "", property_type: "single_family",
    beds: 0, baths: 0, living_sqft: 0, has_clean_title: true,
    loan_type: "fix_and_flip", purchase_price: 0, expected_loan_amount: 0,
    estimated_rehab_budget: 0, arv: 0, down_payment: 0,
    monthly_rent: 0, monthly_expenses: 0, notes: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form, sponsor_slug: ref };
      ["num_projects","credit_score","capital_available","beds","baths","living_sqft","purchase_price","expected_loan_amount","estimated_rehab_budget","arv","down_payment","monthly_rent","monthly_expenses"].forEach((k) => {
        payload[k] = Number(payload[k] || 0);
      });
      const { data } = await api.post("/public/apply", payload);
      setDone(data);
      toast.success("Application received");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4">
        <Card className="w-full max-w-xl border-slate-800 bg-slate-900 p-8 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
          <h1 className="mt-4 font-display text-3xl">{t("intake.doneTitle")}</h1>
          <p className="mt-2 text-sm text-slate-400">
            {t("intake.doneBody")}
          </p>
          <div className="mt-6 rounded-md border border-slate-800 bg-slate-950 p-4 text-left">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">{t("intake.doneScoreLabel")}</div>
            <AiScoreBadge score={done.ai_score} testId="result-ai-score" />
            <div className="mt-3 font-mono text-[11px] text-slate-400">Ref: {done.application_id}</div>
          </div>
          <Link to="/"><Button className="mt-6 bg-emerald-600 text-white hover:bg-emerald-500" data-testid="intake-done-home-btn">Back to home</Button></Link>
        </Card>
      </div>
    );
  }

  const canNext = () => {
    if (step === 0) return form.borrower_name && form.borrower_email;
    if (step === 2) return form.property_address;
    return true;
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <header className="border-b border-slate-800 bg-slate-950">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="intake-home-link">
            <Logo height={88} />
          </Link>
          {ref && <div className="text-xs text-slate-400">{t("intake.referredBy")} <span className="font-semibold text-emerald-300">{ref}</span></div>}
          <LanguageToggle />
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6 py-10">
        <h1 className="font-display text-4xl">{t("intake.title")}</h1>
        <p className="mt-2 text-sm text-slate-400">{t("intake.subtitle")}</p>

        <div className="mt-8">
          <div className="mb-2 flex items-center justify-between text-xs">
            <span className="text-slate-300">Step {step + 1} of {STEPS.length} · {STEPS[step]}</span>
            <span className="text-slate-500">{Math.round(((step + 1) / STEPS.length) * 100)}%</span>
          </div>
          <Progress value={((step + 1) / STEPS.length) * 100} className="h-1 bg-slate-800" />
        </div>

        <Card className="mt-6 border-slate-800 bg-slate-900 p-6" data-testid={`intake-step-${step}`}>
          {step === 0 && <StepPersonal form={form} update={update} />}
          {step === 1 && <StepExperience form={form} update={update} />}
          {step === 2 && <StepProperty form={form} update={update} />}
          {step === 3 && <StepFinancial form={form} update={update} />}

          <div className="mt-8 flex justify-between">
            <Button
              variant="outline"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800 disabled:opacity-40"
              data-testid="intake-back-btn"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> {t("common.back")}
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                onClick={() => setStep((s) => s + 1)}
                disabled={!canNext()}
                className="bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60"
                data-testid="intake-next-btn"
              >
                {t("common.next")} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={busy}
                className="bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60"
                data-testid="intake-submit-btn"
              >
                {busy ? t("intake.submitting") : t("intake.submit")}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Field({ label, children, span = 1 }) {
  return (
    <div className={span === 2 ? "sm:col-span-2" : ""}>
      <Label className="text-slate-300">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function StepPersonal({ form, update }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field label="Full Name" span={2}>
        <Input value={form.borrower_name} onChange={(e) => update("borrower_name", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-name" />
      </Field>
      <Field label="Email">
        <Input type="email" value={form.borrower_email} onChange={(e) => update("borrower_email", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-email" />
      </Field>
      <Field label="Phone">
        <Input value={form.borrower_phone} onChange={(e) => update("borrower_phone", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-phone" />
      </Field>
      <Field label="LLC Name (optional)" span={2}>
        <Input value={form.llc_name} onChange={(e) => update("llc_name", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-llc" />
      </Field>
      <div className="sm:col-span-2 flex items-center gap-2">
        <Checkbox checked={form.owns_home} onCheckedChange={(v) => update("owns_home", !!v)} className="border-slate-700 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid="intake-owns-home" />
        <Label className="text-slate-300 cursor-pointer" onClick={() => update("owns_home", !form.owns_home)}>Own primary residence</Label>
      </div>
      <div className="sm:col-span-2 flex items-center gap-2">
        <Checkbox checked={form.property_in_llc} onCheckedChange={(v) => update("property_in_llc", !!v)} className="border-slate-700 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid="intake-in-llc" />
        <Label className="text-slate-300 cursor-pointer" onClick={() => update("property_in_llc", !form.property_in_llc)}>Investment property will be held in LLC</Label>
      </div>
    </div>
  );
}

function StepExperience({ form, update }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field label="Credit Score">
        <Input type="number" value={form.credit_score} onChange={(e) => update("credit_score", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-credit" />
      </Field>
      <Field label="Available Capital ($)">
        <Input type="number" value={form.capital_available} onChange={(e) => update("capital_available", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-capital" />
      </Field>
      <div className="sm:col-span-2 flex items-center gap-2">
        <Checkbox checked={form.has_re_experience} onCheckedChange={(v) => update("has_re_experience", !!v)} className="border-slate-700 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid="intake-has-exp" />
        <Label className="text-slate-300 cursor-pointer" onClick={() => update("has_re_experience", !form.has_re_experience)}>I have real estate investment experience</Label>
      </div>
      <Field label="Projects completed (last 36 months)">
        <Input type="number" value={form.num_projects} onChange={(e) => update("num_projects", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-projects" />
      </Field>
      <Field label="Primary experience type">
        <Select value={form.experience_type} onValueChange={(v) => update("experience_type", v)}>
          <SelectTrigger className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-exp-type">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
            <SelectItem value="fix_and_flip">Fix & Flip</SelectItem>
            <SelectItem value="rental">Rental / Buy & Hold</SelectItem>
            <SelectItem value="ground_up">Ground-Up Construction</SelectItem>
            <SelectItem value="wholesale">Wholesaling</SelectItem>
            <SelectItem value="none">No prior experience</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

function StepProperty({ form, update }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field label="Property Address" span={2}>
        <Input value={form.property_address} onChange={(e) => update("property_address", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-address" />
      </Field>
      <Field label="Property Type">
        <Select value={form.property_type} onValueChange={(v) => update("property_type", v)}>
          <SelectTrigger className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-ptype">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
            <SelectItem value="single_family">Single Family</SelectItem>
            <SelectItem value="multifamily">Multifamily</SelectItem>
            <SelectItem value="condo">Condo / Townhome</SelectItem>
            <SelectItem value="commercial">Commercial</SelectItem>
            <SelectItem value="land">Land</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Living Sq. Ft.">
        <Input type="number" value={form.living_sqft} onChange={(e) => update("living_sqft", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-sqft" />
      </Field>
      <Field label="Beds">
        <Input type="number" value={form.beds} onChange={(e) => update("beds", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-beds" />
      </Field>
      <Field label="Baths">
        <Input type="number" step="0.5" value={form.baths} onChange={(e) => update("baths", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-baths" />
      </Field>
      <div className="sm:col-span-2 flex items-center gap-2">
        <Checkbox checked={form.has_clean_title} onCheckedChange={(v) => update("has_clean_title", !!v)} className="border-slate-700 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid="intake-clean-title" />
        <Label className="text-slate-300 cursor-pointer" onClick={() => update("has_clean_title", !form.has_clean_title)}>Property has a clean title</Label>
      </div>
    </div>
  );
}

function StepFinancial({ form, update }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field label="Loan Type">
        <Select value={form.loan_type} onValueChange={(v) => update("loan_type", v)}>
          <SelectTrigger className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-loan-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
            {LOAN_PROGRAMS.map((p) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Purchase Price ($)">
        <Input type="number" value={form.purchase_price} onChange={(e) => update("purchase_price", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-purchase" />
      </Field>
      <Field label="Expected Loan Amount ($)">
        <Input type="number" value={form.expected_loan_amount} onChange={(e) => update("expected_loan_amount", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-loan-amt" />
      </Field>
      <Field label="Rehab Budget ($)">
        <Input type="number" value={form.estimated_rehab_budget} onChange={(e) => update("estimated_rehab_budget", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-rehab" />
      </Field>
      <Field label="ARV ($)">
        <Input type="number" value={form.arv} onChange={(e) => update("arv", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-arv" />
      </Field>
      <Field label="Down Payment ($)">
        <Input type="number" value={form.down_payment} onChange={(e) => update("down_payment", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-down" />
      </Field>
      {form.loan_type === "dscr" && (
        <>
          <Field label="Monthly Rent ($)">
            <Input type="number" value={form.monthly_rent} onChange={(e) => update("monthly_rent", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-rent" />
          </Field>
          <Field label="Monthly Expenses ($)">
            <Input type="number" value={form.monthly_expenses} onChange={(e) => update("monthly_expenses", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-expenses" />
          </Field>
        </>
      )}
      <Field label="Notes" span={2}>
        <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} className="border-slate-800 bg-slate-950 text-slate-50" data-testid="intake-notes" />
      </Field>
    </div>
  );
}
